import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChargeValidationRow, ChargeValidationWarningDetail } from './models';
import { describeWarning } from './ChargeValidationService';
import styles from './ChargeValidation.module.scss';

type Props = {
    /** Linhas do mesmo grupo. Numa despesa isolada é sempre uma só. */
    rows: ChargeValidationRow[];
};

/**
 * Task #66220: detalhe dos avisos por bullets — um bloco por problema, com título, os valores em
 * confronto e uma nota de ajuda — em vez de uma frase única separada por ";".
 */
const ChargeValidationWarningList: React.FC<Props> = ({ rows }: Props) => {
    const { t } = useTranslation();

    // Avisos iguais em linhas diferentes do grupo aparecem uma só vez; os que não cobrem todas as
    // linhas do grupo levam o número da linha à frente do título, para não haver dúvidas.
    const details: ChargeValidationWarningDetail[] = [];
    const legacyMessages: string[] = [];
    const seen = new Map<string, { detail: ChargeValidationWarningDetail; lines: number[] }>();

    rows.forEach(row => {
        row.warnings.forEach(warning => {
            const detail = describeWarning(warning, t);
            if (!detail) {
                if (warning.message && !legacyMessages.includes(warning.message)) {
                    legacyMessages.push(warning.message);
                }
                return;
            }
            const existing = seen.get(detail.key);
            if (existing) {
                existing.lines.push(row.line);
                return;
            }
            const entry = { detail, lines: [row.line] };
            seen.set(detail.key, entry);
            details.push(detail);
        });
    });

    if (rows.length > 1) {
        details.forEach(detail => {
            const entry = seen.get(detail.key);
            if (entry && entry.lines.length < rows.length) {
                detail.linePrefix = `${t('import_charges.line')} ${entry.lines.join(', ')}`;
            }
        });
    }

    return (
        <>
            {details.map(detail => (
                <div key={detail.key} className={styles.warningBlock}>
                    <div className={styles.warningTitle}>
                        {detail.linePrefix ? <span className={styles.warningLinePrefix}>{detail.linePrefix} · </span> : <></>}
                        {detail.title}
                    </div>
                    <div className={styles.warningRows}>
                        {detail.rows.map(row => (
                            <React.Fragment key={row.label}>
                                <span className={styles.warningRowLabel}>{row.label}:</span>
                                <span className={styles.warningRowValue}>{row.value}</span>
                            </React.Fragment>
                        ))}
                    </div>
                    {detail.hint ? <div className={styles.warningHint}>{detail.hint}</div> : <></>}
                </div>
            ))}
            {legacyMessages.map(message => (
                <div key={message} className={styles.warningLegacy}>{message}</div>
            ))}
        </>
    );
};

export default ChargeValidationWarningList;
